<template>
  <b-modal
    :id="modalId"
    :visible="show"
    :title="isEditMode ? 'تعديل نقطة ثابتة' : 'إضافة نقطة ثابتة'"
    size="lg"
    scrollable
    content-class="mobile-responsive-modal"
    header-class="mobile-modal-header"
    body-class="mobile-modal-body"
    footer-class="mobile-modal-footer"
    no-close-on-backdrop
    @hide="handleClose"
    @ok="handleSubmit"
  >
    <validation-observer ref="form">
      <b-form @submit.prevent>
        <b-row>
          <!-- Accuracy Degree (Required) -->
          <b-col
            cols="12"
            md="6"
          >
            <validation-provider
              #default="{ errors }"
              name="accuracy_degree"
              rules="required"
            >
              <b-form-group label-for="accuracy_degree">
                <v-select
                  v-model="form.accuracy_degree"
                  :options="accuracyDegreeOptions"
                  :reduce="(val) => val.value"
                  :get-option-label="(option) => option.label_ar"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  placeholder="اختر درجة الرصد *"
                >
                  <template #option="{ label_ar, color }">
                    <span :style="{ color }">{{ label_ar }}</span>
                  </template>
                  <template #selected-option="{ label_ar }">
                    <span>{{ label_ar }}</span>
                  </template>
                </v-select>
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <!-- Level -->
          <!-- <b-col cols="12" md="6">
            <validation-provider #default="{ errors }" name="level">
              <b-form-group  label-for="level">
                <b-form-input
                  id="level"
                  v-model.number="form.level"
                  type="number"
                  step="any"
                  min="1"
                  max="999.999"
                  placeholder="المستوى"
                />
                <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col> -->

          <!-- Responsible Employee -->
          <b-col
            cols="12"
            md="6"
          >
            <validation-provider
              #default="{ errors }"
              name="responsible_employee_id"
            >
              <b-form-group label-for="responsible_employee_id">
                <v-select
                  v-model="form.responsible_employee_id"
                  :options="employees.data || []"
                  :reduce="(val) => val.id"
                  :get-option-label="(option) => option.full_name"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  placeholder="اختر الموظف المسؤول *"
                  @search="callEmployees"
                >
                  <template #option="{ full_name }">
                    <span>{{ full_name }}</span>
                  </template>
                  <template #selected-option="{ full_name }">
                    <span>{{ full_name }}</span>
                  </template>
                </v-select>
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <!-- Monitoring Team -->
          <b-col
            cols="12"
            md="6"
          >
            <validation-provider
              #default="{ errors }"
              name="monitoring_team_ids"
            >
              <b-form-group label-for="monitoring_team_ids">
                <v-select
                  v-model="form.monitoring_team_ids"
                  :options="employees.data || []"
                  :reduce="(val) => val.id"
                  :get-option-label="(option) => option.full_name"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  placeholder="اختر فريق الرصد"
                  multiple
                  :close-on-select="false"
                  @search="callEmployees"
                  @input="onMonitoringTeamChange"
                >
                  <template #option="{ full_name }">
                    <span>{{ full_name }}</span>
                  </template>
                  <template #selected-option="{ full_name }">
                    <span>{{ full_name }}</span>
                  </template>
                </v-select>
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <!-- Description & Location Section -->
        <b-row>
          <!-- Description -->
          <b-col md="12">
            <validation-provider
              #default="{ errors }"
              name="description"
            >
              <b-form-group label-for="description">
                <b-form-input
                  id="description"
                  v-model="form.description"
                  type="text"
                  placeholder="وصف النقطة الثابتة (اختياري)"
                />
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <!-- Governorate -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label-for="governorate_id">
              <v-select
                v-model="form.governorate_id"
                :options="governorates || []"
                :reduce="val => val.id"
                :get-option-label="option => option.name_ar"
                :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                placeholder="اختر المحافظة"
                @search="onGovernorateSearch"
              >
                <template #option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
                <template #selected-option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <!-- Center -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label-for="center_id">
              <v-select
                v-model="form.center_id"
                :options="centers || []"
                :reduce="val => val.id"
                :get-option-label="option => option.name_ar"
                :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                placeholder="اختر الجهاز / الحي / المركز"
                @search="onCenterSearch"
              >
                <template #option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
                <template #selected-option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <!-- City -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label-for="city_id">
              <v-select
                v-model="form.city_id"
                :options="cities || []"
                :reduce="val => val.id"
                :get-option-label="option => option.name_ar"
                :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                placeholder="اختر الوحدة المحلية / المدينة"
                @search="onCitySearch"
              >
                <template #option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
                <template #selected-option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <!-- District -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label-for="district_id">
              <v-select
                v-model="form.district_id"
                :options="districts || []"
                :reduce="val => val.id"
                :get-option-label="option => option.name_ar"
                :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                placeholder="اختر القرية / التجمع"
                @search="onDistrictsSearch"
              >
                <template #option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
                <template #selected-option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <!-- Village -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label-for="village_id">
              <v-select
                v-model="form.village_id"
                :options="villages || []"
                :reduce="val => val.id"
                :get-option-label="option => option.name_ar"
                :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                placeholder="اختر عزبة / نجع / منطقة"
                @search="onVillageSearch"
              >
                <template #option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
                <template #selected-option="{ name_ar }">
                  <span class="ml-50">{{ name_ar }}</span>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Coordinate Type Selection -->
        <b-row>
          <b-col cols="12">
            <validation-provider
              #default="{ errors }"
              name="coord_type"
              rules="required"
            >
              <b-form-group
                label="نوع الإحداثيات *"
                label-for="coord_type"
              >
                <v-select
                  v-model="form.coord_type"
                  :options="coordinateTypes"
                  :reduce="(val) => val.value"
                  :get-option-label="(option) => option.label_ar"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  placeholder="اختر نوع الإحداثيات"
                  @input="onCoordTypeChange"
                >
                  <template #option="{ label_ar }">
                    <span>{{ label_ar }}</span>
                  </template>
                  <template #selected-option="{ label_ar }">
                    <span>{{ label_ar }}</span>
                  </template>
                </v-select>
                <small
                  v-if="errors[0]"
                  class="text-danger"
                >{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <!-- Correction Type Selection -->
        <!-- <b-row class="mt-2">
          <b-col cols="12">
            <b-form-group label="حالة الإحداثيات">
              <b-form-radio-group
                v-model="form.correction_type"
                :options="correctionTypeOptions"
                name="coordinate-correction-type"
                @input="onCorrectionTypeChange"
                :disabled="isCorrectionTypeLocked"
                stacked
              ></b-form-radio-group>
              <small v-if="isCorrectionTypeLocked" class="text-muted">يتم تحديد نوع التصحيح تلقائياً إلى "بعد التصحيح" عند درجة الرصد VRS</small>
              <small v-if="correctionTypeApiError" class="text-danger">{{ correctionTypeApiError }}</small>
            </b-form-group>
          </b-col>
        </b-row> -->
        <b-row>
          <b-col cols="12">
            <b-form-checkbox
              v-model="form.checked"
              @change="onCheckedChange"
            >
              تم المراجعة
            </b-form-checkbox>
          </b-col>
        </b-row>
        <!-- Coordinates Section - Before Correction -->
        <b-row class="mt-2">
          <b-col cols="12">
            <h6
              v-if="form.coord_type === 'lat_lon'"
              class="mb-2"
            >
              الإحداثيات الجغرافية (Lat/Lng)
            </h6>
            <h6
              v-else-if="form.coord_type === 'etm'"
              class="mb-2"
            >
              إحداثيات ETM
            </h6>
            <h6
              v-else-if="form.coord_type === 'utm'"
              class="mb-2"
            >
              إحداثيات UTM
            </h6>
          </b-col>
          <!-- Latitude/Longitude Fields - Show only when coord_type = "lat_lon" -->
          <template v-if="form.coord_type === 'lat_lon'">
            <b-col
              cols="12"
              md="6"
            >
              <validation-provider
                #default="{ errors }"
                name="latitude"
                :rules="form.coord_type === 'lat_lon' ? 'required' : ''"
              >
                <b-form-group
                  label="خط العرض (Latitude) *"
                  label-for="latitude"
                >
                  <b-form-input
                    id="latitude"
                    v-model.number="form.latitude"
                    type="number"
                    step="any"
                    min="-90"
                    max="90"
                    placeholder="مثال: 30.0444"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <validation-provider
                #default="{ errors }"
                name="longitude"
                :rules="form.coord_type === 'lat_lon' ? 'required' : ''"
              >
                <b-form-group
                  label="خط الطول (Longitude) *"
                  label-for="longitude"
                >
                  <b-form-input
                    id="longitude"
                    v-model.number="form.longitude"
                    type="number"
                    step="any"
                    min="-180"
                    max="180"
                    placeholder="مثال: 31.2357"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </template>

          <!-- ETM Fields - Show only when coord_type = "etm" -->
          <template v-if="form.coord_type === 'etm'">
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="etm_est"
                :rules="form.coord_type === 'etm' ? 'required' : ''"
              >
                <b-form-group
                  label="ETM East *"
                  label-for="etm_est"
                >
                  <b-form-input
                    id="etm_est"
                    v-model.number="form.etm_est"
                    type="number"
                    step="any"
                    placeholder="ETM East"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="etm_north"
                :rules="form.coord_type === 'etm' ? 'required' : ''"
              >
                <b-form-group
                  label="ETM North *"
                  label-for="etm_north"
                >
                  <b-form-input
                    id="etm_north"
                    v-model.number="form.etm_north"
                    type="number"
                    step="any"
                    placeholder="ETM North"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="etm_zone"
              >
                <b-form-group
                  label="ETM Zone"
                  label-for="etm_zone"
                >
                  <b-form-input
                    id="etm_zone"
                    v-model="form.etm_zone"
                    placeholder="مثال: 36N"
                    maxlength="10"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </template>

          <!-- UTM Fields - Show only when coord_type = "utm" -->
          <template v-if="form.coord_type === 'utm'">
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="utm_est"
                :rules="form.coord_type === 'utm' ? 'required' : ''"
              >
                <b-form-group label-for="utm_est">
                  <b-form-input
                    id="utm_est"
                    v-model.number="form.utm_est"
                    type="number"
                    step="any"
                    placeholder="UTM East *"
                    class="utm-est-input"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="utm_north"
                :rules="form.coord_type === 'utm' ? 'required' : ''"
              >
                <b-form-group label-for="utm_north">
                  <b-form-input
                    id="utm_north"
                    v-model.number="form.utm_north"
                    type="number"
                    step="any"
                    placeholder="UTM North *"
                    class="utm-north-input"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <!-- Index -->
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="index"
              >
                <b-form-group label-for="index">
                  <b-form-input
                    id="index"
                    v-model.number="form.index"
                    type="number"
                    step="0.001"
                    min="0"
                    placeholder="المنسوب (اختياري)"
                    class="index-input"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="3"
            >
              <validation-provider
                #default="{ errors }"
                name="utm_zone"
              >
                <b-form-group
                  label="UTM Zone"
                  label-for="utm_zone"
                >
                  <b-form-input
                    id="utm_zone"
                    v-model="form.utm_zone"
                    placeholder="مثال: 36N"
                    maxlength="10"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </template>

        </b-row>

        <!-- Coordinates Section - After Correction -->
        <b-row
          v-if="form.checked"
          class="mt-2"
        >
          <b-col cols="12">
            <h6
              v-if="form.coord_type === 'lat_lon'"
              class="mb-2"
            >
              الإحداثيات الجغرافية المصححة (Corrected Lat/Lng)
            </h6>
            <h6
              v-else-if="form.coord_type === 'etm'"
              class="mb-2"
            >
              إحداثيات ETM المصححة
            </h6>
            <h6
              v-else-if="form.coord_type === 'utm'"
              class="mb-2"
            >
              إحداثيات UTM المصححة
            </h6>
          </b-col>

          <!-- Corrected Latitude/Longitude Fields -->
          <template v-if="form.coord_type === 'lat_lon'">
            <b-col
              cols="12"
              md="6"
            >
              <validation-provider
                #default="{ errors }"
                name="corrected_latitude"
              >
                <b-form-group
                  label="خط العرض المصحح (Corrected Latitude) *"
                  label-for="corrected_latitude"
                >
                  <b-form-input
                    id="corrected_latitude"
                    v-model.number="form.corrected_latitude"
                    type="number"
                    step="any"
                    min="-90"
                    max="90"
                    placeholder="مثال: 30.0445"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <validation-provider
                #default="{ errors }"
                name="corrected_longitude"
              >
                <b-form-group
                  label="خط الطول المصحح (Corrected Longitude) *"
                  label-for="corrected_longitude"
                >
                  <b-form-input
                    id="corrected_longitude"
                    v-model.number="form.corrected_longitude"
                    type="number"
                    step="any"
                    min="-180"
                    max="180"
                    placeholder="مثال: 31.2358"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </template>

          <!-- Corrected ETM Fields -->
          <template v-if="form.coord_type === 'etm'">
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="corrected_etm_est"
              >
                <b-form-group
                  label="ETM East المصحح *"
                  label-for="corrected_etm_est"
                >
                  <b-form-input
                    id="corrected_etm_est"
                    v-model.number="form.corrected_etm_est"
                    type="number"
                    step="any"
                    placeholder="ETM East المصحح"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="corrected_etm_north"
              >
                <b-form-group
                  label="ETM North المصحح *"
                  label-for="corrected_etm_north"
                >
                  <b-form-input
                    id="corrected_etm_north"
                    v-model.number="form.corrected_etm_north"
                    type="number"
                    step="any"
                    placeholder="ETM North المصحح"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="corrected_etm_zone"
              >
                <b-form-group label-for="corrected_etm_zone">
                  <b-form-input
                    id="corrected_etm_zone"
                    v-model="form.corrected_etm_zone"
                    placeholder="مثال: 36N"
                    maxlength="10"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </template>

          <!-- Corrected UTM Fields -->
          <template v-if="form.coord_type === 'utm'">
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="corrected_utm_est"
              >
                <b-form-group label-for="corrected_utm_est">
                  <b-form-input
                    id="corrected_utm_est"
                    v-model.number="form.corrected_utm_est"
                    type="number"
                    step="any"
                    placeholder="UTM East المصحح *"
                    class="utm-est-input"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="corrected_utm_north"
              >
                <b-form-group label-for="corrected_utm_north">
                  <b-form-input
                    id="corrected_utm_north"
                    v-model.number="form.corrected_utm_north"
                    type="number"
                    step="any"
                    placeholder="UTM North المصحح *"
                    class="utm-north-input"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="index"
              >
                <b-form-group label-for="index">
                  <b-form-input
                    id="index"
                    v-model.number="form.index"
                    type="number"
                    step="0.001"
                    min="0"
                    placeholder="المنسوب (اختياري)"
                    class="index-input"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="corrected_utm_zone"
              >
                <b-form-group label-for="corrected_utm_zone">
                  <b-form-input
                    id="corrected_utm_zone"
                    v-model="form.corrected_utm_zone"
                    placeholder="مثال: 36N"
                    maxlength="10"
                  />
                  <small
                    v-if="errors[0]"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </template>
        </b-row>

        <!-- Media Files Section -->
        <b-row
          v-if="shouldShowMainMediaInputs"
          class="mt-3"
        >
          <b-col cols="12">
            <h6 class="mb-2">
              ملفات الوسائط المطلوبة
            </h6>
          </b-col>

          <!-- RINEX Media -->
          <b-col
            v-if="shouldShowRinexInput"
            cols="12"
            md="6"
          >
            <b-form-group
              label="ملف RINEX"
              label-for="rinex_file_upload"
            >
              <b-form-file
                id="rinex_file_upload"
                v-model="uploadedFiles.rinex_file"
                class="file-input-rinex"
                placeholder="اختر ملف RINEX للرفع..."
                @change="handleRinexFileChange"
              />
              <div
                v-if="isEditMode && fixedPoint && fixedPoint.rinex_media"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="font-weight-bold text-break">
                  {{ fixedPoint.rinex_media.original_name || fixedPoint.rinex_media.name || 'ملف RINEX الحالي' }}
                </div>
                <small class="text-muted d-block">
                  {{ fixedPoint.rinex_media.human_readable_size || formatFileSize(fixedPoint.rinex_media.size) }}
                </small>
                <b-button
                  v-if="fixedPoint.rinex_media.download_url || fixedPoint.rinex_media.url"
                  variant="outline-primary"
                  size="sm"
                  class="mt-1"
                  :href="fixedPoint.rinex_media.download_url || fixedPoint.rinex_media.url"
                  target="_blank"
                >
                  تحميل الملف الحالي
                </b-button>
              </div>

              <!-- File Preview -->
              <div
                v-if="uploadedFiles.rinex_file"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="d-flex align-items-center justify-content-between flex-wrap">
                  <div class="flex-grow-1 mb-2 mb-md-0">
                    <div class="font-weight-bold text-break">
                      {{ uploadedFiles.rinex_file.name }}
                    </div>
                    <small class="text-muted">{{ formatFileSize(uploadedFiles.rinex_file.size) }}</small>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="ml-2"
                    @click="removeUploadedFile('rinex_file')"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>
            </b-form-group>
          </b-col>

          <!-- Raw Data Media -->
          <b-col
            v-if="shouldShowRawDataInput"
            cols="12"
            md="6"
          >
            <b-form-group
              label="(raw data)ملف البيانات الخام"
              label-for="raw_data_file_upload"
            >
              <b-form-file
                id="raw_data_file_upload"
                v-model="uploadedFiles.raw_data_file"
                class="file-input-raw"
                placeholder="اختر ملف البيانات الخام للرفع..."
                @change="handleRawDataFileChange"
              />
              <div
                v-if="isEditMode && fixedPoint && fixedPoint.raw_data_media"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="font-weight-bold text-break">
                  {{ fixedPoint.raw_data_media.original_name || fixedPoint.raw_data_media.name || 'ملف البيانات الخام الحالي' }}
                </div>
                <small class="text-muted d-block">
                  {{ fixedPoint.raw_data_media.human_readable_size || formatFileSize(fixedPoint.raw_data_media.size) }}
                </small>
                <b-button
                  v-if="fixedPoint.raw_data_media.download_url || fixedPoint.raw_data_media.url"
                  variant="outline-primary"
                  size="sm"
                  class="mt-1"
                  :href="fixedPoint.raw_data_media.download_url || fixedPoint.raw_data_media.url"
                  target="_blank"
                >
                  تحميل الملف الحالي
                </b-button>
              </div>
              <!-- File Preview -->
              <div
                v-if="uploadedFiles.raw_data_file"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="d-flex align-items-center justify-content-between flex-wrap">
                  <div class="flex-grow-1 mb-2 mb-md-0">
                    <div class="font-weight-bold text-break">
                      {{ uploadedFiles.raw_data_file.name }}
                    </div>
                    <small class="text-muted">{{ formatFileSize(uploadedFiles.raw_data_file.size) }}</small>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="ml-2"
                    @click="removeUploadedFile('raw_data_file')"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>
            </b-form-group>
          </b-col>

          <!-- Control Project Archive -->
          <b-col
            v-if="shouldShowProjectControlInput"
            cols="12"
            md="6"
          >
            <b-form-group
              label="ملف المشروع (project)"
              label-for="dwg_file_upload"
            >
              <b-form-file
                id="dwg_file_upload"
                v-model="uploadedFiles.DWG_file"
                class="file-input-dwg"
                accept=".zip,.rar,application/zip,application/x-rar-compressed,application/vnd.rar"
                placeholder="اختر ملف المشروع (project) (zip/rar)"
                @change="handleDwgFileChange"
              />
              <div
                v-if="isEditMode && fixedPoint && (fixedPoint.dwg_media || fixedPoint.DWG_media)"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="font-weight-bold text-break">
                  {{ (fixedPoint.dwg_media || fixedPoint.DWG_media).original_name || (fixedPoint.dwg_media || fixedPoint.DWG_media).name || 'ملف الكنترول الحالي' }}
                </div>
                <small class="text-muted d-block">
                  {{ (fixedPoint.dwg_media || fixedPoint.DWG_media).human_readable_size || formatFileSize((fixedPoint.dwg_media || fixedPoint.DWG_media).size) }}
                </small>
                <b-button
                  v-if="(fixedPoint.dwg_media || fixedPoint.DWG_media).download_url || (fixedPoint.dwg_media || fixedPoint.DWG_media).url"
                  variant="outline-primary"
                  size="sm"
                  class="mt-1"
                  :href="(fixedPoint.dwg_media || fixedPoint.DWG_media).download_url || (fixedPoint.dwg_media || fixedPoint.DWG_media).url"
                  target="_blank"
                >
                  تحميل الملف الحالي
                </b-button>
              </div>
              <!-- File Preview -->
              <div
                v-if="uploadedFiles.DWG_file"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="d-flex align-items-center justify-content-between flex-wrap">
                  <div class="flex-grow-1 mb-2 mb-md-0">
                    <div class="font-weight-bold text-break">
                      {{ uploadedFiles.DWG_file.name }}
                    </div>
                    <small class="text-muted">{{ formatFileSize(uploadedFiles.DWG_file.size) }}</small>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="ml-2"
                    @click="removeUploadedFile('DWG_file')"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Corrected Specific Media Files Section - After Correction -->
        <b-row
          v-if="shouldShowCorrectedSpecificMediaInputs"
          class="mt-3"
        >
          <b-col cols="12">
            <h6 class="mb-2">
              ملفات الوسائط المصححة المرتبطة
            </h6>
          </b-col>

          <!-- Corrected RINEX Media -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="ملف RINEX المصحح"
              class="file-input-corrected-rinex"
              label-for="corrected_rinex_file_upload"
            >
              <b-form-file
                id="corrected_rinex_file_upload"
                v-model="uploadedFiles.corrected_rinex_file"

                placeholder="اختر ملف RINEX المصحح للرفع..."
                @change="handleCorrectedRinexFileChange"
              />
              <div
                v-if="isEditMode && fixedPoint && fixedPoint.corrected_rinex_media"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="font-weight-bold text-break">
                  {{ fixedPoint.corrected_rinex_media.original_name || fixedPoint.corrected_rinex_media.name || 'ملف RINEX المصحح الحالي' }}
                </div>
                <small class="text-muted d-block">
                  {{ fixedPoint.corrected_rinex_media.human_readable_size || formatFileSize(fixedPoint.corrected_rinex_media.size) }}
                </small>
                <b-button
                  v-if="fixedPoint.corrected_rinex_media.download_url || fixedPoint.corrected_rinex_media.url"
                  variant="outline-primary"
                  size="sm"
                  class="mt-1"
                  :href="fixedPoint.corrected_rinex_media.download_url || fixedPoint.corrected_rinex_media.url"
                  target="_blank"
                >
                  تحميل الملف الحالي
                </b-button>
              </div>
              <!-- File Preview -->
              <div
                v-if="uploadedFiles.corrected_rinex_file"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="d-flex align-items-center justify-content-between flex-wrap">
                  <div class="flex-grow-1 mb-2 mb-md-0">
                    <div class="font-weight-bold text-break">
                      {{ uploadedFiles.corrected_rinex_file.name }}
                    </div>
                    <small class="text-muted">{{ formatFileSize(uploadedFiles.corrected_rinex_file.size) }}</small>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="ml-2"
                    @click="removeUploadedFile('corrected_rinex_file')"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>
            </b-form-group>
          </b-col>

          <!-- Corrected Raw Data Media -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="ملف البيانات الخام المصحح"
              label-for="corrected_raw_data_file_upload"
            >
              <b-form-file
                id="corrected_raw_data_file_upload"
                v-model="uploadedFiles.corrected_raw_data_file"
                class="file-input-corrected-raw"
                placeholder="اختر ملف البيانات الخام المصحح للرفع..."
                @change="handleCorrectedRawDataFileChange"
              />
              <div
                v-if="isEditMode && fixedPoint && fixedPoint.corrected_raw_data_media"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="font-weight-bold text-break">
                  {{ fixedPoint.corrected_raw_data_media.original_name || fixedPoint.corrected_raw_data_media.name || 'ملف البيانات الخام المصحح الحالي' }}
                </div>
                <small class="text-muted d-block">
                  {{ fixedPoint.corrected_raw_data_media.human_readable_size || formatFileSize(fixedPoint.corrected_raw_data_media.size) }}
                </small>
                <b-button
                  v-if="fixedPoint.corrected_raw_data_media.download_url || fixedPoint.corrected_raw_data_media.url"
                  variant="outline-primary"
                  size="sm"
                  class="mt-1"
                  :href="fixedPoint.corrected_raw_data_media.download_url || fixedPoint.corrected_raw_data_media.url"
                  target="_blank"
                >
                  تحميل الملف الحالي
                </b-button>
              </div>
              <!-- File Preview -->
              <div
                v-if="uploadedFiles.corrected_raw_data_file"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="d-flex align-items-center justify-content-between flex-wrap">
                  <div class="flex-grow-1 mb-2 mb-md-0">
                    <div class="font-weight-bold text-break">
                      {{ uploadedFiles.corrected_raw_data_file.name }}
                    </div>
                    <small class="text-muted">{{ formatFileSize(uploadedFiles.corrected_raw_data_file.size) }}</small>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="ml-2"
                    @click="removeUploadedFile('corrected_raw_data_file')"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>
            </b-form-group>
          </b-col>

          <!-- Corrected DWG Media -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="ملف DWG المصحح (AutoCAD)"
              label-for="corrected_dwg_file_upload"
            >
              <b-form-file
                id="corrected_dwg_file_upload"
                v-model="uploadedFiles.corrected_DWG_file"
                class="file-input-corrected-dwg"
                placeholder="اختر ملف DWG المصحح للرفع..."
                @change="handleCorrectedDwgFileChange"
              />
              <div
                v-if="isEditMode && fixedPoint && (fixedPoint.corrected_dwg_media || fixedPoint.corrected_DWG_media)"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="font-weight-bold text-break">
                  {{ (fixedPoint.corrected_dwg_media || fixedPoint.corrected_DWG_media).original_name || (fixedPoint.corrected_dwg_media || fixedPoint.corrected_DWG_media).name || 'ملف DWG المصحح الحالي' }}
                </div>
                <small class="text-muted d-block">
                  {{ (fixedPoint.corrected_dwg_media || fixedPoint.corrected_DWG_media).human_readable_size || formatFileSize((fixedPoint.corrected_dwg_media || fixedPoint.corrected_DWG_media).size) }}
                </small>
                <b-button
                  v-if="(fixedPoint.corrected_dwg_media || fixedPoint.corrected_DWG_media).download_url || (fixedPoint.corrected_dwg_media || fixedPoint.corrected_DWG_media).url"
                  variant="outline-primary"
                  size="sm"
                  class="mt-1"
                  :href="(fixedPoint.corrected_dwg_media || fixedPoint.corrected_DWG_media).download_url || (fixedPoint.corrected_dwg_media || fixedPoint.corrected_DWG_media).url"
                  target="_blank"
                >
                  تحميل الملف الحالي
                </b-button>
              </div>
              <!-- File Preview -->
              <div
                v-if="uploadedFiles.corrected_DWG_file"
                class="mt-2 p-2 border rounded bg-light"
              >
                <div class="d-flex align-items-center justify-content-between flex-wrap">
                  <div class="flex-grow-1 mb-2 mb-md-0">
                    <div class="font-weight-bold text-break">
                      {{ uploadedFiles.corrected_DWG_file.name }}
                    </div>
                    <small class="text-muted">{{ formatFileSize(uploadedFiles.corrected_DWG_file.size) }}</small>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="ml-2"
                    @click="removeUploadedFile('corrected_DWG_file')"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Corrected Bulk Media Files Section - After Correction -->
        <b-row
          v-if="shouldShowCorrectedBulkMediaInputs"
          class="mt-3"
        >
          <b-col cols="12">
            <h6 class="mb-2">
              ملفات الوسائط المصححة المجمعة
            </h6>
            <b-form-group>
              <b-form-file
                v-model="correctedMediaFiles"
                class="file-input-corrected-bulk"
                multiple
                placeholder="اختر الملفات المصححة..."
                @change="handleCorrectedMediaFileChange"
              />
            </b-form-group>

            <!-- Selected Corrected Media Files Preview -->
            <div
              v-if="correctedMediaFiles.length > 0"
              class="mt-2"
            >
              <div
                v-for="(file, index) in correctedMediaFiles"
                :key="index"
                class="d-flex flex-column p-2 mb-2 border rounded"
              >
                <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-2">
                  <div class="flex-grow-1 mb-2 mb-md-0 w-100 w-md-auto">
                    <div class="font-weight-bold text-break">
                      {{ file.name }}
                    </div>
                    <small class="text-muted">{{ formatFileSize(file.size) }}</small>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="w-100 w-md-auto"
                    @click="removeCorrectedMediaFile(index)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                    إزالة
                  </b-button>
                </div>
                <div class="d-flex flex-column flex-md-row gap-2 w-100">
                  <b-form-select
                    v-model="correctedMediaTypes[index]"
                    :options="correctedMediaTypeOptions"
                    placeholder="اختر النوع"
                    class="flex-grow-1"
                  >
                    <template #first>
                      <option :value="null">
                        اختر النوع (اختياري)
                      </option>
                    </template>
                  </b-form-select>
                  <b-form-input
                    v-model="correctedMediaNotes[index]"
                    placeholder="ملاحظات (اختياري)"
                    class="flex-grow-1"
                  />
                </div>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- File Upload Section -->
        <b-row class="mt-3">
          <b-col cols="12">
            <h6 class="mb-2">
              الملفات المرفقة
            </h6>
            <b-form-group>
              <b-form-file
                v-model="mediaFiles"
                class="file-input-general-media"
                multiple
                accept="image/*,application/pdf"
                placeholder="اختر الملفات..."
                @change="handleFileChange"
              />
            </b-form-group>

            <!-- Selected Files Preview -->
            <div
              v-if="mediaFiles.length > 0"
              class="mt-2"
            >
              <div
                v-for="(file, index) in mediaFiles"
                :key="index"
                class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between p-2 mb-2 border rounded"
              >
                <div class="flex-grow-1 mb-2 mb-md-0 w-100 w-md-auto">
                  <div class="font-weight-bold text-break">
                    {{ file.name }}
                  </div>
                  <small class="text-muted">
                    {{ formatFileSize(file.size) }}
                  </small>
                </div>
                <div class="d-flex align-items-center gap-2 w-100 w-md-auto">
                  <b-form-input
                    v-model="mediaNames[index]"
                    placeholder="اسم الملف (اختياري)"
                    size="sm"
                    class="flex-grow-1 flex-md-grow-0"
                    style="min-width: 150px;"
                  />
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="removeFile(index)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>
            </div>

            <!-- Existing Media (for edit mode) -->
            <div
              v-if="isEditMode && existingMedia.length > 0"
              class="mt-3"
            >
              <h6 class="mb-2">
                الملفات الحالية
              </h6>
              <div class="d-flex flex-wrap gap-2">
                <div
                  v-for="media in existingMedia"
                  :key="media.id"
                  class="position-relative"
                >
                  <b-avatar
                    v-b-tooltip.hover.top="media.name"
                    :src="media.url"
                    size="64px"
                    rounded
                  />
                  <b-button
                    variant="danger"
                    size="sm"
                    class="position-absolute"
                    style="top: -5px; right: -5px;"
                    @click="removeExistingMedia(media.id)"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="10"
                    />
                  </b-button>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>

    <template #modal-footer>
      <div class="d-flex flex-column flex-sm-row gap-2 w-100">
        <b-button
          variant="secondary"
          class="w-100 w-sm-auto order-2 order-sm-1"
          @click="handleClose"
        >
          إلغاء
        </b-button>
        <b-button
          variant="primary"
          :disabled="load"
          class="w-100 w-sm-auto order-1 order-sm-2"
          @click="handleSubmit"
        >
          <b-spinner
            v-if="load"
            small
            class="mr-1"
          />
          {{ isEditMode ? 'تحديث' : 'إضافة' }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormFile,
  BFormSelect,
  BFormCheckbox,
  BFormRadioGroup,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BAvatar,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import {
  required, email, phone, national_id,
} from '@validations'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import request from '@/utils/request'
import Media from '@/api/projects/media'

export default {
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormSelect,
    BFormCheckbox,
    BFormRadioGroup,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAvatar,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    FeatherIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    fixedPoint: {
      type: Object,
      default: null,
    },
    modalId: {
      type: String,
      default: 'fixed-point-form-modal',
    },
  },
  computed: {
    ...mapGetters({
      employees: 'employees/employees',
      load: 'fixedPoints/load',
      governorates: 'governorates/governorates',
      centers: 'centers/centers',
      cities: 'cities/cities',
      districts: 'districts/districts',
      villages: 'villages/districts',
      accuracyDegrees: 'settings/accuracyDegrees',
      loadAccuracyDegrees: 'settings/loadAccuracyDegrees',
    }),
    isEditMode() {
      return !!this.fixedPoint
    },
    accuracyDegreeOptions() {
      return this.accuracyDegrees
    },
    selectedAccuracyDegreeOption() {
      return (this.accuracyDegreeOptions || []).find(
        option => option?.value === this.form.accuracy_degree,
      ) || null
    },
    isVrsAccuracy() {
      const currentValue = this.form.accuracy_degree
      const currentLabel = this.selectedAccuracyDegreeOption?.label_ar || this.selectedAccuracyDegreeOption?.label || ''
      return [currentValue, currentLabel].some(
        val => typeof val === 'string' && val.toLowerCase().includes('vrs'),
      )
    },
    isStaticAccuracy() {
      const currentValue = this.form.accuracy_degree
      const currentLabel = this.selectedAccuracyDegreeOption?.label_ar || this.selectedAccuracyDegreeOption?.label || ''
      return [currentValue, currentLabel].some(
        val => typeof val === 'string' && val.toLowerCase().includes('static'),
      )
    },
    shouldShowMainMediaInputs() {
      return this.isVrsAccuracy || this.form.correction_type === 'before' || this.form.checked
    },
    shouldShowRinexInput() {
      return !this.isVrsAccuracy && (this.form.correction_type === 'before' || this.form.checked)
    },
    shouldShowRawDataInput() {
      return this.isVrsAccuracy || this.form.correction_type === 'before' || this.form.checked
    },
    shouldShowProjectControlInput() {
      return this.isVrsAccuracy || this.form.correction_type === 'before' || this.form.checked
    },
    shouldShowCorrectedSpecificMediaInputs() {
      if (this.isVrsAccuracy || this.isStaticAccuracy) return false
      return this.form.correction_type === 'after' || this.form.checked
    },
    shouldShowCorrectedBulkMediaInputs() {
      if (this.isVrsAccuracy) return false
      return this.form.correction_type === 'after' || this.form.checked
    },
    isCorrectionTypeLocked() {
      return this.isVrsAccuracy
    },
    existingMedia() {
      return this.fixedPoint?.media || []
    },
  },
  data() {
    return {
      form: {
        coord_type: 'utm', // Default coordinate type
        correction_type: 'before',
        latitude: null,
        longitude: null,
        etm_est: null,
        etm_north: null,
        etm_zone: null,
        utm_est: null,
        utm_north: null,
        utm_zone: null,
        // Corrected coordinates
        corrected_latitude: null,
        corrected_longitude: null,
        corrected_etm_est: null,
        corrected_etm_north: null,
        corrected_etm_zone: null,
        corrected_utm_est: null,
        corrected_utm_north: null,
        corrected_utm_zone: null,
        checked: false,
        level: null,
        accuracy_degree: null,
        responsible_employee_id: null,
        monitoring_team_ids: [],
        description: null,
        governorate_id: null,
        center_id: null,
        city_id: null,
        district_id: null,
        village_id: null,
        index: null,
        rinex_media_id: null,
        raw_data_media_id: null,
        DWG_media_id: null,
      },
      correctionTypeOptions: [
        { text: 'قبل التصحيح', value: 'before' },
        { text: 'بعد التصحيح', value: 'after' },
      ],
      correctionTypeApiError: null,
      coordinateTypes: [],
      mediaFiles: [],
      mediaNames: [],
      mediaToDelete: [],
      mediaList: [],
      loadingMedia: false,
      mediaApi: new Media(),
      mediaSearchTimeout: null,
      uploadedFiles: {
        rinex_file: null,
        raw_data_file: null,
        DWG_file: null,
        corrected_rinex_file: null,
        corrected_raw_data_file: null,
        corrected_DWG_file: null,
      },
      correctedMediaFiles: [],
      correctedMediaTypes: [],
      correctedMediaNotes: [],
      correctedMediaTypeOptions: [
        { value: 'rinex', text: 'RINEX' },
        { value: 'raw_data', text: 'البيانات الخام' },
        { value: 'dwg', text: 'DWG' },
        { value: 'other', text: 'أخرى' },
      ],
      isPopulatingLocationFields: false,
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.initForm()
      }
    },
    fixedPoint: {
      handler(newVal) {
        if (newVal) {
          this.populateForm(newVal)
        }
      },
      immediate: true,
    },
    'form.accuracy_degree': {
      handler() {
        this.applyCorrectionTypeRule()
        this.syncMediaInputsByRules()
      },
      immediate: true,
    },
    'form.correction_type': function () {
      this.syncMediaInputsByRules()
      if (this.form.correction_type !== 'after') {
        this.form.checked = false
      }
    },
    'form.checked': function (newVal) {
      if (newVal) {
        this.form.correction_type = 'after'
      } else if (!this.isCorrectionTypeLocked) {
        this.form.correction_type = 'before'
      }
    },
    'form.governorate_id': function (newVal, oldVal) {
      if (this.isPopulatingLocationFields) {
        if (newVal) this.$store.dispatch('centers/list', { governorate_id: newVal })
        return
      }
      if (newVal === oldVal) return
      this.form.center_id = null
      this.form.city_id = null
      this.form.district_id = null
      this.form.village_id = null
      if (newVal) this.$store.dispatch('centers/list', { governorate_id: newVal })
    },
    'form.center_id': function (newVal, oldVal) {
      if (this.isPopulatingLocationFields) {
        if (newVal) this.$store.dispatch('cities/list', { center_id: newVal })
        return
      }
      if (newVal === oldVal) return
      this.form.city_id = null
      this.form.district_id = null
      this.form.village_id = null
      if (newVal) this.$store.dispatch('cities/list', { center_id: newVal })
    },
    'form.city_id': function (newVal, oldVal) {
      if (this.isPopulatingLocationFields) {
        if (newVal) this.$store.dispatch('districts/list', { city_id: newVal })
        return
      }
      if (newVal === oldVal) return
      this.form.district_id = null
      this.form.village_id = null
      if (newVal) this.$store.dispatch('districts/list', { city_id: newVal })
    },
    'form.district_id': function (newVal, oldVal) {
      if (this.isPopulatingLocationFields) {
        if (newVal) this.$store.dispatch('villages/list', { district_id: newVal })
        return
      }
      if (newVal === oldVal) return
      this.form.village_id = null
      if (newVal) this.$store.dispatch('villages/list', { district_id: newVal })
    },
  },
  mounted() {
    this.initForm()
    this.$store.dispatch('employees/listEmployees', { query: {} })
    // Preload governorates so the first dropdown has data
    this.$store.dispatch('settings/listAccuracyDegrees', {})
    this.$store.dispatch('governorates/list', {})
    this.fetchCoordinateTypes()
    this.fetchMediaList()
  },
  methods: {
    initForm() {
      this.correctionTypeApiError = null
      this.form = {
        coord_type: 'utm', // Default to lat_lon
        correction_type: 'before',
        latitude: null,
        longitude: null,
        etm_est: null,
        etm_north: null,
        etm_zone: null,
        utm_est: null,
        utm_north: null,
        utm_zone: null,
        // Corrected coordinates
        corrected_latitude: null,
        corrected_longitude: null,
        corrected_etm_est: null,
        corrected_etm_north: null,
        corrected_etm_zone: null,
        corrected_utm_est: null,
        corrected_utm_north: null,
        corrected_utm_zone: null,
        checked: false,
        level: null,
        accuracy_degree: null,
        responsible_employee_id: null,
        monitoring_team_ids: [],
        description: null,
        governorate_id: null,
        center_id: null,
        city_id: null,
        district_id: null,
        village_id: null,
        index: null,
        rinex_media_id: null,
        raw_data_media_id: null,
        DWG_media_id: null,
      }
      this.mediaFiles = []
      this.mediaNames = []
      this.mediaToDelete = []
      this.uploadedFiles = {
        rinex_file: null,
        raw_data_file: null,
        DWG_file: null,
        corrected_rinex_file: null,
        corrected_raw_data_file: null,
        corrected_DWG_file: null,
      }
      this.correctedMediaFiles = []
      this.correctedMediaTypes = []
      this.correctedMediaNotes = []

      if (this.fixedPoint) {
        this.populateForm(this.fixedPoint)
      }
    },
    populateForm(fixedPoint) {
      this.isPopulatingLocationFields = true
      // Determine coord_type based on existing data
      let coordType = 'lat_lon' // Default
      if (fixedPoint.coord_type) {
        coordType = fixedPoint.coord_type
      } else if (fixedPoint.etm_est && fixedPoint.etm_north) {
        coordType = 'etm'
      } else if (fixedPoint.utm_est && fixedPoint.utm_north) {
        coordType = 'utm'
      } else if (fixedPoint.latitude && fixedPoint.longitude) {
        coordType = 'lat_lon'
      }

      // Determine correction type: if corrected coordinates exist, default to 'after', otherwise 'before'
      const correctionType = this.normalizeCorrectionType(fixedPoint.correction_type)

      this.form = {
        coord_type: coordType,
        correction_type: correctionType,
        latitude: fixedPoint.latitude || null,
        longitude: fixedPoint.longitude || null,
        etm_est: fixedPoint.etm_est || null,
        etm_north: fixedPoint.etm_north || null,
        etm_zone: fixedPoint.etm_zone || null,
        utm_est: fixedPoint.utm_est || null,
        utm_north: fixedPoint.utm_north || null,
        utm_zone: fixedPoint.utm_zone || null,
        // Corrected coordinates
        corrected_latitude: fixedPoint.corrected_latitude || null,
        corrected_longitude: fixedPoint.corrected_longitude || null,
        corrected_etm_est: fixedPoint.corrected_etm_est || null,
        corrected_etm_north: fixedPoint.corrected_etm_north || null,
        corrected_etm_zone: fixedPoint.corrected_etm_zone || null,
        corrected_utm_est: fixedPoint.corrected_utm_est || null,
        corrected_utm_north: fixedPoint.corrected_utm_north || null,
        corrected_utm_zone: fixedPoint.corrected_utm_zone || null,
        checked: correctionType === 'after' ? !!fixedPoint.checked : false,
        level: fixedPoint.level || null,
        accuracy_degree: fixedPoint.accuracy_degree || null,
        responsible_employee_id: fixedPoint.responsible_employee_id || null,
        monitoring_team_ids: this.normalizeMonitoringTeamIds(
          fixedPoint.monitoring_teams || fixedPoint.monitoring_team_ids || [],
        ),
        description: fixedPoint.description || null,
        governorate_id: this.normalizeLocationId(fixedPoint.governorate_id),
        center_id: this.normalizeLocationId(fixedPoint.center_id),
        city_id: this.normalizeLocationId(fixedPoint.city_id),
        district_id: this.normalizeLocationId(fixedPoint.district_id),
        village_id: this.normalizeLocationId(fixedPoint.village_id),
        index: fixedPoint.index !== undefined && fixedPoint.index !== null ? fixedPoint.index : null,
        rinex_media_id: fixedPoint.rinex_media_id || null,
        raw_data_media_id: fixedPoint.raw_data_media_id || null,
        DWG_media_id: fixedPoint.DWG_media_id || null,
      }
      this.$nextTick(() => {
        this.isPopulatingLocationFields = false
      })
    },
    async fetchCoordinateTypes() {
      try {
        const response = await request({
          url: 'data/coordinate-types',
          method: 'get',
        })
        if (response.success && response.data) {
          this.coordinateTypes = response.data
        }
      } catch (error) {
        console.error('Error fetching coordinate types:', error)
        // Fallback to default types if API fails
        this.coordinateTypes = [
          { value: 'utm', label: 'UTM', label_ar: 'UTM' },
          { value: 'lat_lon', label: 'Latitude/Longitude', label_ar: 'خطوط الطول والعرض' },
          { value: 'etm', label: 'ETM', label_ar: 'ETM' },
        ]
      }
    },
    onCoordTypeChange(newType) {
      // Clear fields that are not relevant to the new type
      if (newType !== 'lat_lon') {
        this.form.latitude = null
        this.form.longitude = null
      }
      if (newType !== 'etm') {
        this.form.etm_est = null
        this.form.etm_north = null
        this.form.etm_zone = null
      }
      if (newType !== 'utm') {
        this.form.utm_est = null
        this.form.utm_north = null
        this.form.utm_zone = null
      }
    },
    normalizeLocationId(value) {
      if (value === null || value === undefined || value === '') return null
      const num = Number(value)
      return Number.isNaN(num) ? value : num
    },
    normalizeCorrectionType(value) {
      return value === 'after' ? 'after' : 'before'
    },
    applyCorrectionTypeRule() {
      if (this.isCorrectionTypeLocked) {
        this.form.correction_type = 'after'
        this.correctionTypeApiError = null
      } else {
        this.form.correction_type = this.normalizeCorrectionType(this.form.correction_type)
      }
    },
    onCorrectionTypeChange(value) {
      if (this.isCorrectionTypeLocked) {
        this.form.correction_type = 'after'
        return
      }
      this.form.correction_type = this.normalizeCorrectionType(value)
      if (this.form.correction_type !== 'after') {
        this.form.checked = false
      }
      this.correctionTypeApiError = null
    },
    async onCheckedChange(checked) {
      if (!checked) {
        this.form.checked = false
        return
      }

      const result = await this.$swal({
        title: 'هل أنت متأكد؟',
        text: 'سيتم تحديد النقطة على أنها تمت مراجعتها',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'نعم',
        cancelButtonText: 'لا',
        confirmButtonColor: '#06284D',
        cancelButtonColor: '#6c757d',
      })

      if (!result?.isConfirmed) {
        this.$nextTick(() => {
          this.form.checked = false
        })
        return
      }
      this.form.checked = true
    },
    handleFileChange(event) {
      const files = Array.from(event.target.files || [])
      this.mediaFiles = files
      // Initialize names array
      this.mediaNames = files.map(file => file.name)
    },
    removeFile(index) {
      this.mediaFiles.splice(index, 1)
      this.mediaNames.splice(index, 1)
    },
    removeExistingMedia(mediaId) {
      this.mediaToDelete.push(mediaId)
      // Remove from existing media display
      const index = this.existingMedia.findIndex(m => m.id === mediaId)
      if (index > -1) {
        this.existingMedia.splice(index, 1)
      }
    },
    callEmployees(search) {
      if (search) {
        const query = search ? { search } : {}
        this.$store.dispatch('employees/listEmployees', { query })
      }
    },
    onMonitoringTeamChange(value) {
      this.form.monitoring_team_ids = this.normalizeMonitoringTeamIds(value)
    },
    normalizeMonitoringTeamIds(value) {
      const ids = (Array.isArray(value) ? value : [])
        .map(item => (typeof item === 'object' && item !== null ? item.id : item))
        .map(id => Number(id))
        .filter(id => Number.isFinite(id))
      return [...new Set(ids)]
    },
    onGovernorateSearch(search) {
      if (search) {
        const params = search ? { search } : {}
        this.$store.dispatch('governorates/list', params)
      }
    },
    onCenterSearch(search) {
      const params = search ? { search } : {}
      this.$store.dispatch('centers/list', params)
    },
    onCitySearch(search) {
      const params = search ? { search } : {}
      this.$store.dispatch('cities/list', params)
    },
    onDistrictsSearch(search) {
      const params = search ? { search } : {}
      this.$store.dispatch('districts/list', params)
    },
    onVillageSearch(search) {
      const params = search ? { search } : {}
      this.$store.dispatch('villages/list', params)
    },
    async handleSubmit() {
      this.correctionTypeApiError = null
      const isValid = await this.$refs.form.validate()
      if (!isValid) {
        return
      }

      // Create FormData
      const formData = new FormData()

      // Append coord_type (required)
      formData.append('coord_type', this.form.coord_type)
      formData.append('correction_type', this.normalizeCorrectionType(this.form.correction_type))
      formData.append('checked', this.form.correction_type === 'after' && this.form.checked ? 1 : 0)

      // Append coordinate fields based on coord_type
      if (this.form.coord_type === 'lat_lon') {
        if (this.form.latitude !== null && this.form.latitude !== undefined) {
          formData.append('latitude', this.form.latitude)
        }
        if (this.form.longitude !== null && this.form.longitude !== undefined) {
          formData.append('longitude', this.form.longitude)
        }
      } else if (this.form.coord_type === 'etm') {
        if (this.form.etm_est !== null && this.form.etm_est !== undefined) {
          formData.append('etm_est', this.form.etm_est)
        }
        if (this.form.etm_north !== null && this.form.etm_north !== undefined) {
          formData.append('etm_north', this.form.etm_north)
        }
        if (this.form.etm_zone !== null && this.form.etm_zone !== undefined && this.form.etm_zone !== '') {
          formData.append('etm_zone', this.form.etm_zone)
        }
      } else if (this.form.coord_type === 'utm') {
        if (this.form.utm_est !== null && this.form.utm_est !== undefined) {
          formData.append('utm_est', this.form.utm_est)
        }
        if (this.form.utm_north !== null && this.form.utm_north !== undefined) {
          formData.append('utm_north', this.form.utm_north)
        }
        if (this.form.utm_zone !== null && this.form.utm_zone !== undefined && this.form.utm_zone !== '') {
          formData.append('utm_zone', this.form.utm_zone)
        }
      }

      // Append corrected coordinates
      const correctedCoordFields = [
        'corrected_latitude',
        'corrected_longitude',
        'corrected_etm_est',
        'corrected_etm_north',
        'corrected_etm_zone',
        'corrected_utm_est',
        'corrected_utm_north',
        'corrected_utm_zone',
      ]
      correctedCoordFields.forEach(key => {
        if (this.form[key] !== null && this.form[key] !== undefined && this.form[key] !== '') {
          formData.append(key, this.form[key])
        }
      })

      // Append other form fields
      const otherFields = [
        'level',
        'accuracy_degree',
        'responsible_employee_id',
        'description',
        'governorate_id',
        'center_id',
        'city_id',
        'district_id',
        'village_id',
        'index',
      ]
      otherFields.forEach(key => {
        if (this.form[key] !== null && this.form[key] !== undefined && this.form[key] !== '') {
          formData.append(key, this.form[key])
        }
      })

      const monitoringTeamIds = this.normalizeMonitoringTeamIds(this.form.monitoring_team_ids)
      this.form.monitoring_team_ids = monitoringTeamIds
      monitoringTeamIds.forEach(id => {
        formData.append('monitoring_team_ids[]', id)
      })

      // Handle media IDs - always send them (even if null) to allow removing associations
      const mediaFields = ['rinex_media_id', 'raw_data_media_id', 'DWG_media_id']
      mediaFields.forEach(key => {
        if (this.form[key] !== null && this.form[key] !== undefined && this.form[key] !== '') {
          formData.append(key, this.form[key])
        } else {
          // Send empty string for null values (backend should convert to null)
          formData.append(key, '')
        }
      })

      // Append uploaded files (rinex_file, raw_data_file, DWG_file)
      if (this.uploadedFiles.rinex_file) {
        formData.append('rinex_file', this.uploadedFiles.rinex_file)
      }
      if (this.uploadedFiles.raw_data_file) {
        formData.append('raw_data_file', this.uploadedFiles.raw_data_file)
      }
      if (this.uploadedFiles.DWG_file) {
        formData.append('DWG_file', this.uploadedFiles.DWG_file)
      }

      // Append corrected specific media files
      if (this.uploadedFiles.corrected_rinex_file) {
        formData.append('corrected_rinex_file', this.uploadedFiles.corrected_rinex_file)
      }
      if (this.uploadedFiles.corrected_raw_data_file) {
        formData.append('corrected_raw_data_file', this.uploadedFiles.corrected_raw_data_file)
      }
      if (this.uploadedFiles.corrected_DWG_file) {
        formData.append('corrected_DWG_file', this.uploadedFiles.corrected_DWG_file)
      }

      // Append corrected bulk media files
      this.correctedMediaFiles.forEach((file, index) => {
        formData.append('corrected_media[]', file)
        if (this.correctedMediaTypes[index]) {
          formData.append('corrected_media_types[]', this.correctedMediaTypes[index])
        }
        if (this.correctedMediaNotes[index]) {
          formData.append('corrected_media_notes[]', this.correctedMediaNotes[index])
        }
      })

      // Append media files (general attachments)
      this.mediaFiles.forEach((file, index) => {
        formData.append(`media[${index}]`, file)
        if (this.mediaNames[index]) {
          formData.append(`media_names[${index}]`, this.mediaNames[index])
        }
      })

      try {
        if (this.isEditMode) {
          await this.$store.dispatch('fixedPoints/updateFixedPoint', {
            id: this.fixedPoint.id,
            formData,
          })
          this.$toast.success('تم تحديث النقطة الثابتة بنجاح')
        } else {
          await this.$store.dispatch('fixedPoints/createFixedPoint', { formData })
          this.$toast.success('تم إضافة النقطة الثابتة بنجاح')
        }
        this.$emit('saved')
        this.handleClose()
      } catch (error) {
        const correctionTypeErrors = error?.response?.data?.errors?.correction_type
        if (Array.isArray(correctionTypeErrors) && correctionTypeErrors[0]) {
          this.correctionTypeApiError = correctionTypeErrors[0]
        }
        const apiErrors = error?.response?.data?.errors || {}
        const hasMonitoringTeamError = Object.keys(apiErrors).some(key => key.startsWith('monitoring_team_ids'))
        if (hasMonitoringTeamError) {
          this.$toast.error('One or more selected monitoring team members are invalid.')
          return
        }
        const errorMessage = error?.response?.data?.message
          || error?.response?.data?.errors
          || 'حدث خطأ أثناء الحفظ'
        this.$toast.error(errorMessage)
      }
    },
    handleClose() {
      this.$emit('close')
      this.initForm()
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`
    },
    async fetchMediaList(search = '') {
      this.loadingMedia = true
      try {
        const query = search ? { search } : {}
        const response = await this.mediaApi.list(query)
        if (response && response.success !== false) {
          // Handle paginated response
          if (response.data && Array.isArray(response.data.data)) {
            this.mediaList = response.data.data
          } else if (response.data && Array.isArray(response.data)) {
            this.mediaList = response.data
          } else if (Array.isArray(response)) {
            this.mediaList = response
          } else {
            this.mediaList = []
          }
        } else {
          this.mediaList = []
        }
      } catch (error) {
        console.error('Error fetching media list:', error)
        this.mediaList = []
      } finally {
        this.loadingMedia = false
      }
    },
    onMediaSearch(search) {
      // Debounce search to avoid too many API calls
      if (this.mediaSearchTimeout) {
        clearTimeout(this.mediaSearchTimeout)
      }
      this.mediaSearchTimeout = setTimeout(() => {
        if (search && search.length >= 2) {
          this.fetchMediaList(search)
        } else if (!search || search.length === 0) {
          this.fetchMediaList()
        }
      }, 300)
    },
    validateFileSize(file, maxSizeMB = 900) {
      const fileSizeMB = file.size / 1024 / 1024
      if (fileSizeMB > maxSizeMB) {
        this.$toast.error(`حجم الملف يتجاوز الحد الأقصى المسموح به (${maxSizeMB}MB)`)
        return false
      }
      return true
    },
    validateProjectControlArchive(file) {
      const name = (file?.name || '').toLowerCase()
      const isValidArchive = name.endsWith('.zip') || name.endsWith('.rar')
      if (!isValidArchive) {
        this.$toast.error('ملف المشروعات من الكنترول يجب أن يكون بصيغة zip أو rar')
        return false
      }
      return true
    },
    handleRinexFileChange(event) {
      const file = event.target.files?.[0]
      if (file) {
        if (this.validateFileSize(file)) {
          this.uploadedFiles.rinex_file = file
        } else {
          // Reset file input
          event.target.value = ''
        }
      }
    },
    handleRawDataFileChange(event) {
      const file = event.target.files?.[0]
      if (file) {
        if (this.validateFileSize(file)) {
          this.uploadedFiles.raw_data_file = file
        } else {
          // Reset file input
          event.target.value = ''
        }
      }
    },
    handleDwgFileChange(event) {
      const file = event.target.files?.[0]
      if (file) {
        if (this.validateFileSize(file) && this.validateProjectControlArchive(file)) {
          this.uploadedFiles.DWG_file = file
        } else {
          // Reset file input
          event.target.value = ''
        }
      }
    },
    syncMediaInputsByRules() {
      if (!this.shouldShowRinexInput) {
        this.uploadedFiles.rinex_file = null
      }
      if (!this.shouldShowRawDataInput) {
        this.uploadedFiles.raw_data_file = null
      }
      if (!this.shouldShowProjectControlInput) {
        this.uploadedFiles.DWG_file = null
      }
      if (!this.shouldShowCorrectedSpecificMediaInputs) {
        this.uploadedFiles.corrected_rinex_file = null
        this.uploadedFiles.corrected_raw_data_file = null
        this.uploadedFiles.corrected_DWG_file = null
      }
      if (!this.shouldShowCorrectedBulkMediaInputs) {
        this.correctedMediaFiles = []
        this.correctedMediaTypes = []
        this.correctedMediaNotes = []
      }
    },
    removeUploadedFile(fileType) {
      this.uploadedFiles[fileType] = null
      // Reset file input
      const inputIdMap = {
        rinex_file: 'rinex_file_upload',
        raw_data_file: 'raw_data_file_upload',
        DWG_file: 'dwg_file_upload',
        corrected_rinex_file: 'corrected_rinex_file_upload',
        corrected_raw_data_file: 'corrected_raw_data_file_upload',
        corrected_DWG_file: 'corrected_dwg_file_upload',
      }
      const inputId = inputIdMap[fileType] || ''
      const input = document.getElementById(inputId)
      if (input) {
        input.value = ''
      }
    },
    handleCorrectedRinexFileChange(event) {
      const file = event.target.files?.[0]
      if (file) {
        if (this.validateFileSize(file)) {
          this.uploadedFiles.corrected_rinex_file = file
        } else {
          event.target.value = ''
        }
      }
    },
    handleCorrectedRawDataFileChange(event) {
      const file = event.target.files?.[0]
      if (file) {
        if (this.validateFileSize(file)) {
          this.uploadedFiles.corrected_raw_data_file = file
        } else {
          event.target.value = ''
        }
      }
    },
    handleCorrectedDwgFileChange(event) {
      const file = event.target.files?.[0]
      if (file) {
        if (this.validateFileSize(file)) {
          this.uploadedFiles.corrected_DWG_file = file
        } else {
          event.target.value = ''
        }
      }
    },
    handleCorrectedMediaFileChange(event) {
      const files = Array.from(event.target.files || [])
      this.correctedMediaFiles = files
      // Initialize types and notes arrays
      this.correctedMediaTypes = new Array(files.length).fill(null)
      this.correctedMediaNotes = new Array(files.length).fill('')

      // Validate file sizes
      files.forEach((file, index) => {
        if (!this.validateFileSize(file)) {
          this.correctedMediaFiles.splice(index, 1)
          this.correctedMediaTypes.splice(index, 1)
          this.correctedMediaNotes.splice(index, 1)
        }
      })
    },
    removeCorrectedMediaFile(index) {
      this.correctedMediaFiles.splice(index, 1)
      this.correctedMediaTypes.splice(index, 1)
      this.correctedMediaNotes.splice(index, 1)
    },
  },
}
</script>

<style scoped>
.border {
  border: 1px solid #dee2e6 !important;
}

/* Different background colors for each input field */
.utm-est-input {
  background-color: #e3f2fd !important; /* Light blue */
}

.utm-north-input {
  background-color: #f3e5f5 !important; /* Light purple */
}

.index-input {
  background-color: #e8f5e9 !important; /* Light green */
}

/* Different background colors for file inputs */
::v-deep .file-input-rinex  {
  background-color: #0f8be4;
}

::v-deep .file-input-raw  {
  background-color: #f20808 !important;
}

::v-deep .file-input-raw-alt  {
  background-color: #8c2d4c;
}

::v-deep .file-input-dwg  {
  background-color: #7b2cf2;
}

::v-deep .file-input-corrected-rinex  {
  background-color: #4ee85b;
}

::v-deep .file-input-corrected-raw  {
  background-color: #507329;
}

::v-deep .file-input-corrected-dwg  {
  background-color: #41e9ff;
}

::v-deep .file-input-corrected-bulk  {
  background-color: #ff84d6;
}

::v-deep .file-input-general-media  {
  background-color: #ffc91a;
}

/* Mobile optimizations */
@media (max-width: 767.98px) {
  .modal-body {
    padding: 0.75rem;
  }

  .form-group {
    margin-bottom: 0.75rem;
  }

  /* Ensure all form inputs are full width on mobile */
  .form-control,
  .custom-file {
    width: 100%;
    font-size: 16px; /* Prevent iOS zoom */
  }

  /* Better spacing for file previews */
  .bg-light {
    padding: 0.75rem !important;
  }

  /* Better text wrapping */
  .text-break {
    word-break: break-word;
    overflow-wrap: break-word;
  }

  /* Column spacing on mobile */
  .row {
    margin-left: -0.5rem;
    margin-right: -0.5rem;
  }

  .row > [class*="col-"] {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    margin-bottom: 0.75rem;
  }

  /* Input fields styling for mobile */
  .utm-est-input,
  .utm-north-input,
  .index-input {
    font-size: 16px !important; /* Prevent iOS zoom */
    padding: 0.625rem 0.75rem !important;
    min-height: 44px; /* Touch-friendly */
  }

  /* v-select mobile styling */
  .v-select {
    width: 100% !important;
  }

  .v-select .vs__dropdown-toggle {
    min-height: 44px; /* Touch-friendly */
    font-size: 16px; /* Prevent iOS zoom */
  }

  .v-select .vs__dropdown-menu {
    max-height: 200px;
  }

  /* Form labels and placeholders */
  .form-group label {
    font-size: 0.9rem;
    margin-bottom: 0.4rem;
  }

  /* Error messages */
  .text-danger {
    font-size: 0.8rem;
    display: block;
    margin-top: 0.25rem;
  }

  /* Buttons in form */
  .btn {
    min-height: 44px; /* Touch-friendly */
    font-size: 0.9rem;
    padding: 0.625rem 1rem;
  }

  /* Coordinate inputs row */
  .row .col-md-4 {
    margin-bottom: 0.75rem;
  }

  /* File upload sections */
  .custom-file {
    min-height: 44px;
  }

  .custom-file-label {
    font-size: 16px;
    padding: 0.625rem 0.75rem;
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  /* File preview sections */
  .bg-light {
    font-size: 0.875rem;
  }

  .bg-light .font-weight-bold {
    font-size: 0.9rem;
  }

  /* Section headings */
  h6 {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  /* Form sections spacing */
  .mt-3 {
    margin-top: 1rem !important;
  }

  /* Better spacing between form rows */
  .row {
    margin-bottom: 0.5rem;
  }
}

/* Tablet optimizations */
@media (min-width: 768px) and (max-width: 991.98px) {
  .form-control,
  .v-select {
    font-size: 15px;
  }

  .row > [class*="col-"] {
    margin-bottom: 0.5rem;
  }
}

/* Ensure v-select is responsive */
@media (max-width: 767.98px) {
  .v-select {
    width: 100% !important;
  }
}
</style>

<style>
/* Global styles for modal responsiveness */
@media (max-width: 767.98px) {
  .mobile-responsive-modal {
    margin: 0.5rem;
    max-width: calc(100% - 1rem) !important;
  }

  .mobile-responsive-modal .modal-content {
    border-radius: 0.5rem;
    max-height: calc(100vh - 1rem);
    display: flex;
    flex-direction: column;
  }

  .mobile-modal-header {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #dee2e6;
  }

  .mobile-modal-header .modal-title {
    font-size: 1.1rem;
    font-weight: 600;
  }

  .mobile-modal-body {
    padding: 0.75rem;
    max-height: calc(100vh - 180px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    flex: 1;
  }

  .mobile-modal-footer {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-top: 1px solid #dee2e6;
  }

  .mobile-modal-footer .btn {
    width: 100%;
    margin: 0;
    min-height: 44px;
    font-size: 0.9rem;
  }

  /* Ensure modal doesn't overflow on small screens */
  .modal-dialog {
    margin: 0.5rem auto;
    max-width: calc(100% - 1rem);
  }
}

/* Touch-friendly improvements */
@media (hover: none) and (pointer: coarse) {
  .form-control,
  .v-select .vs__dropdown-toggle,
  .btn {
    min-height: 44px; /* iOS recommended touch target size */
  }

  .v-select .vs__actions {
    padding: 0.5rem;
  }
}
</style>
